import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  OnDestroy,
} from '@angular/core';

const HORIZONTAL_DRAG_THRESHOLD_PX = 5;
const HORIZONTAL_WHEEL_EASING = 0.42;
const HORIZONTAL_WHEEL_STOP_DISTANCE_PX = 0.5;
const HORIZONTAL_DRAG_IGNORED_TARGETS = 'a, input, select, textarea, video, .core-ui-attachment-card__actions';

@Directive({
  selector: '[coreUiHorizontalDragScroll]',
  exportAs: 'coreUiHorizontalDragScroll',
  standalone: false,
})
export class CoreUiHorizontalDragScrollDirective implements AfterViewInit, OnDestroy {
  @HostBinding('class.is-dragging') isDragging = false;
  @HostBinding('class.can-scroll-previous') canScrollPrevious = false;
  @HostBinding('class.can-scroll-next') canScrollNext = false;

  private pointerId?: number;
  private pointerStartX = 0;
  private scrollStartX = 0;
  private suppressClick = false;
  private clickReleaseTimer?: ReturnType<typeof setTimeout>;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private stateFrame?: number;
  private wheelFrame?: number;
  private wheelTarget?: number;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.elementRef.nativeElement.addEventListener('click', this.onCapturedClick, true);
  }

  @HostBinding('class.is-scrollable')
  get isScrollable(): boolean {
    const element = this.elementRef.nativeElement;
    return element.scrollWidth > element.clientWidth + 1;
  }

  ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement;
    this.resizeObserver = new ResizeObserver(() => this.scheduleScrollStateUpdate());
    this.resizeObserver.observe(element);
    this.mutationObserver = new MutationObserver(() => this.scheduleScrollStateUpdate());
    this.mutationObserver.observe(element, { childList: true, subtree: true });
    this.scheduleScrollStateUpdate();
  }

  @HostListener('scroll')
  onScroll(): void {
    this.updateScrollState();
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (!this.isScrollable || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    const element = this.elementRef.nativeElement;
    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    const currentTarget = this.wheelTarget ?? element.scrollLeft;
    const nextScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, currentTarget + event.deltaY),
    );

    if (nextScrollLeft === currentTarget) {
      if (Math.abs(currentTarget - element.scrollLeft) > HORIZONTAL_WHEEL_STOP_DISTANCE_PX) {
        event.preventDefault();
      }
      return;
    }

    this.wheelTarget = nextScrollLeft;
    this.startWheelAnimation();
    event.preventDefault();
  }

  scrollToAdjacentItem(direction: -1 | 1): void {
    this.stopWheelAnimation();
    const element = this.elementRef.nativeElement;
    const items = Array.from(element.children) as HTMLElement[];
    if (items.length === 0) return;

    const firstItemOffset = items[0].offsetLeft;
    const anchor = element.scrollLeft + firstItemOffset;
    const target = direction > 0
      ? items.find((item) => item.offsetLeft > anchor + 1)
      : [...items].reverse().find((item) => item.offsetLeft < anchor - 1);

    if (!target) return;

    element.scrollTo({
      left: Math.max(0, target.offsetLeft - firstItemOffset),
      behavior: 'smooth',
    });
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    const target = event.target as Element | null;
    if (
      event.pointerType !== 'mouse'
      || event.button !== 0
      || !this.isScrollable
      || target?.closest(HORIZONTAL_DRAG_IGNORED_TARGETS)
    ) {
      return;
    }

    const element = this.elementRef.nativeElement;
    this.stopWheelAnimation();
    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.scrollStartX = element.scrollLeft;
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) return;

    const distance = event.clientX - this.pointerStartX;
    if (!this.isDragging && Math.abs(distance) < HORIZONTAL_DRAG_THRESHOLD_PX) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.elementRef.nativeElement.setPointerCapture?.(event.pointerId);
    }

    this.elementRef.nativeElement.scrollLeft = this.scrollStartX - distance;
    this.updateScrollState();
    event.preventDefault();
  }

  @HostListener('pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) return;

    this.suppressClick = this.isDragging;
    this.releasePointer(event.pointerId);
    this.resetPointer();

    clearTimeout(this.clickReleaseTimer);
    this.clickReleaseTimer = setTimeout(() => {
      this.suppressClick = false;
      this.clickReleaseTimer = undefined;
    });
  }

  @HostListener('pointercancel', ['$event'])
  onPointerCancel(event: PointerEvent): void {
    if (this.pointerId !== event.pointerId) return;

    this.releasePointer(event.pointerId);
    this.resetPointer();
  }

  @HostListener('dragstart', ['$event'])
  onDragStart(event: DragEvent): void {
    if (this.pointerId !== undefined) event.preventDefault();
  }

  ngOnDestroy(): void {
    clearTimeout(this.clickReleaseTimer);
    if (this.stateFrame !== undefined) cancelAnimationFrame(this.stateFrame);
    this.stopWheelAnimation();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.elementRef.nativeElement.removeEventListener('click', this.onCapturedClick, true);
  }

  private readonly onCapturedClick = (event: MouseEvent): void => {
    if (!this.suppressClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.suppressClick = false;
  };

  private releasePointer(pointerId: number): void {
    const element = this.elementRef.nativeElement;
    if (element.hasPointerCapture?.(pointerId)) element.releasePointerCapture(pointerId);
  }

  private resetPointer(): void {
    this.pointerId = undefined;
    this.isDragging = false;
  }

  private scheduleScrollStateUpdate(): void {
    if (this.stateFrame !== undefined) cancelAnimationFrame(this.stateFrame);
    this.stateFrame = requestAnimationFrame(() => {
      this.stateFrame = undefined;
      this.updateScrollState();
    });
  }

  private startWheelAnimation(): void {
    if (this.wheelFrame !== undefined) return;

    const animate = (): void => {
      const element = this.elementRef.nativeElement;
      const target = this.wheelTarget ?? element.scrollLeft;
      const distance = target - element.scrollLeft;

      if (Math.abs(distance) <= HORIZONTAL_WHEEL_STOP_DISTANCE_PX) {
        element.scrollLeft = target;
        this.wheelFrame = undefined;
        this.wheelTarget = undefined;
        this.updateScrollState();
        return;
      }

      element.scrollLeft += distance * HORIZONTAL_WHEEL_EASING;
      this.updateScrollState();
      this.wheelFrame = requestAnimationFrame(animate);
    };

    this.wheelFrame = requestAnimationFrame(animate);
  }

  private stopWheelAnimation(): void {
    if (this.wheelFrame !== undefined) cancelAnimationFrame(this.wheelFrame);
    this.wheelFrame = undefined;
    this.wheelTarget = undefined;
  }

  private updateScrollState(): void {
    const element = this.elementRef.nativeElement;
    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    const canScrollPrevious = element.scrollLeft > 1;
    const canScrollNext = element.scrollLeft < maxScrollLeft - 1;

    if (
      this.canScrollPrevious === canScrollPrevious
      && this.canScrollNext === canScrollNext
    ) {
      return;
    }

    this.canScrollPrevious = canScrollPrevious;
    this.canScrollNext = canScrollNext;
    this.changeDetectorRef.markForCheck();
  }
}
